import { pick } from 'lodash-es'
import { isImageHash } from '#app/lib/boolean_tests'
import { setHas } from '#app/lib/utils'
import { getEntityPathname, type SerializedEntity } from '#entities/lib/entities'
import type { RawWdSearchResult } from '#entities/lib/search/wikidata_search'
import { pluralize } from '#entities/lib/types/entities_types'
import type { SearchResult } from '#server/controllers/search/lib/normalize_result'
import type { RelativeUrl } from '#server/types/common'
import type { EntityUri, WdEntityUri } from '#server/types/entity'
import type { ImageHash, ImagePath } from '#server/types/image'
import { getUserBasePathname } from '#users/lib/users'

export interface SerializedSearchResult {
  id?: string
  uri?: EntityUri
  type: string
  label: string
  description?: string
  typeAlias?: string
  pathname: RelativeUrl
  image?: ImagePath | SerializedEntity['image']['url'] | EntityUri
}

export function serializeSearchResult (result: SearchResult) {
  try {
    let image: ImagePath
    if ('image' in result) {
      image = urlifyImageHash(result.type, result.image)
    }
    const pluralizedType = pluralize(result.type)
    const formatter = typeFormatters[pluralizedType] || entityFormatter(result.type)
    return {
      ...formatter(result),
      image,
    }
  } catch (err) {
    err.context = { result }
    throw err
  }
}

export function serializeEntityAsSearchResult (entity: SerializedEntity) {
  const pluralizedType = pluralize(entity.type)
  return {
    ...pick(entity, [ 'uri', 'id', 'label', 'description', 'pathname' ]),
    type: pluralizedType,
    typeAlias: entity.type,
    image: entity.image.url,
  } as SerializedSearchResult
}

function entityFormatter (typeAlias: string) {
  return function (result: SerializedEntity) {
    return {
      ...result,
      typeAlias,
      pathname: `/entity/${result.uri}`,
    }
  }
}

const typeFormatters = {
  editions: entityFormatter('edition'),
  works: entityFormatter('work'),
  humans: entityFormatter('author'),
  series: entityFormatter('serie'),
  collections: entityFormatter('collection'),
  publishers: entityFormatter('publisher'),
  users (result) {
    result.typeAlias = 'user'
    // label is the username
    result.pathname = getUserBasePathname(result.label)
    return result
  },

  groups (result) {
    result.typeAlias = 'group'
    result.pathname = `/groups/${result.id}`
    return result
  },

  shelves (result) {
    result.typeAlias = 'shelf'
    result.pathname = `/shelves/${result.id}`
    return result
  },

  lists (result) {
    result.typeAlias = 'list'
    result.pathname = `/lists/${result.id}`
    return result
  },

  subjects (result) {
    result.typeAlias = 'subject'
    result.pathname = `/entity/wdt:P921-${result.uri}`
    // Let app/lib/shared/api/img.js request to be redirected
    // to the associated entity image
    result.image = result.uri
    return result
  },
}

export function urlifyImageHash (type: string, hash: ImagePath): ImagePath
export function urlifyImageHash (type: string, hash: ImageHash): ImagePath
export function urlifyImageHash (type: string, hash: unknown) {
  const container = setHas(nonEntityContainersPerType, type) ? type : 'entities'
  if (isImageHash(hash)) {
    return `/img/${container}/${hash}`
  } else {
    return hash
  }
}

const nonEntityContainersPerType = new Set([
  'users',
  'groups',
])

// Pre-formatting is required to set the type
// Taking the opportunity to omit all non-required data
export function serializeSubject (result: RawWdSearchResult): SerializedSearchResult {
  const uri = `wd:${result.id}` as WdEntityUri
  return {
    id: result.id,
    label: result.label,
    description: result.description,
    uri,
    type: 'subjects',
    typeAlias: 'subjects',
    pathname: getEntityPathname(uri),
    image: uri,
  }
}
